import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { deleteDomainById } from '../../containers/settingsSlice';
import { PROTECTED_DOMAIN_FIELDS } from '../../common/fields';
import * as actions from '../../containers/ProtectedDomains/protectedDomainsActions';
import { SERVER_TYPE, TEST_ACCOUNTS } from '@silverfort-nodelibs/common/utils/consts';
import TextInput from '../Controls/TextInput/TextInput';
import SelectField from '../Controls/SelectField/SelectField';
import SwitchField from '../Controls/SwitchField/SwitchField';
import Account from '../Controls/Account/Account';
import Label from '../Controls/Label/Label';
import Description from '../Controls/Description/Description';
import Certificate from '../Controls/Certificate/Certificate';
import { CSR_TYPES } from '../../common/certTypes';

import Button from '../Controls/Button/Button';
import SelectStyles from '../Controls/Select/Select.module.css';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';

import styles from './ProtectedDomain.module.css';
import { httpPost } from '../../../utils';
import { PricingModules, usePricingModulesState } from '../../../../context/contexts/PricingModuleContext';

const ProtectedDomain = ({
    domainId,
    fields,
    dispatch,
    ldapServerOptions,
    domainNodes,
    isNewDomain,
    onDelete,
    allowDelete = true
}) => {
    const storeDispatch = useDispatch();
    const [showDeleteMessage, setShowDeleteMessage] = useState(false);
    const { pricingModulesState } = usePricingModulesState();

    const domainName = fields[PROTECTED_DOMAIN_FIELDS.DOMAIN_NAME.ID];
    const domainShortName = fields[PROTECTED_DOMAIN_FIELDS.DOMAIN_SHORT_NAME.ID];
    const ldapDomainName = fields[PROTECTED_DOMAIN_FIELDS.LDAP_DOMAIN_NAME.ID];
    const ldapIp = fields[PROTECTED_DOMAIN_FIELDS.LDAP_IP.ID];
    const useSecureLdap = fields[PROTECTED_DOMAIN_FIELDS.USE_SECURE_LDAP.ID];
    const backupLdapIp = fields[PROTECTED_DOMAIN_FIELDS.BACKUP_LDAP_IP.ID];
    const assignedNodes = fields[PROTECTED_DOMAIN_FIELDS.ASSIGNED_NODES.ID];
    const adsyncNodes = fields[PROTECTED_DOMAIN_FIELDS.ADSYNC_NODES.ID];
    const useSecureBackupLdap = fields[PROTECTED_DOMAIN_FIELDS.BACKUP_USE_SECURE_LDAP.ID];
    const domainServiceAccount = fields[PROTECTED_DOMAIN_FIELDS.DOMAIN_SERVICE_ACCOUNT.ID];
    const kerberosForwarding = fields[PROTECTED_DOMAIN_FIELDS.KERBEROS_FORWARDING.ID];
    const NTLMForwarding = fields[PROTECTED_DOMAIN_FIELDS.NTLM_FORWARDING.ID];
    const LDAPForwarding = fields[PROTECTED_DOMAIN_FIELDS.LDAP_FORWARDING.ID];
    const LDAPSForwarding = fields[PROTECTED_DOMAIN_FIELDS.LDAPS_FORWARDING.ID];
    const certificateRequest = fields[PROTECTED_DOMAIN_FIELDS.API_CERTIFICATE_REQUEST.ID];
    const certificate = fields[PROTECTED_DOMAIN_FIELDS.API_CERTIFICATE.ID];
    const rootCAcertificate = fields[PROTECTED_DOMAIN_FIELDS.ROOT_CA_CERTIFICATE.ID];

    const domainNodeId = Object.keys(domainNodes)[0];

    const dispatchUpdate = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.UPDATE_DOMAIN,
                payload: { domainId, fieldId, value }
            });
        },
        [dispatch, domainId]
    );

    const dispatchLdapIpAdded = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.LDAP_IP_ADDED,
                payload: { domainId, fieldId, value }
            });
        },
        [dispatch, domainId]
    );

    const updateAndAddValueIfNeeded = (fieldId, value) => {
        if (!value) {
            return dispatchUpdate(fieldId, null);
        }
        const trimmedValue = { ...value, value: value.value.trim(), label: value.label.trim() };
        let existingOption = ldapServerOptions.find(
            (option) => option.value.toLowerCase() === trimmedValue.value.toLowerCase()
        );
        if (!existingOption) {
            dispatchLdapIpAdded(fieldId, trimmedValue);
            existingOption = trimmedValue;
        }
        dispatchUpdate(fieldId, existingOption.value);
    };

    const deleteDomain = async (callback) => {
        if (!isNewDomain) {
            try {
                const response = await httpPost(`/Settings/deleteDomain`, {
                    domainId: domainId
                });
                if (!response.data.result) {
                    return callback(false, 'Failed to delete domain. Please try again later');
                }
            } catch (err) {
                return callback(false, 'Failed to delete domain. Please try again later');
            }
            storeDispatch(deleteDomainById({ deletedId: domainId }));
        }
        dispatch({
            type: actions.DELETE_DOMAIN,
            payload: { domainId }
        });
        if (onDelete) {
            onDelete();
        }
    };

    const handleDeleteClicked = () => (isNewDomain ? deleteDomain() : setShowDeleteMessage(true));

    const getProtocolsError = () => {
        if (kerberosForwarding && kerberosForwarding.error) {
            return kerberosForwarding.error;
        }
        if (NTLMForwarding && NTLMForwarding.error) {
            return NTLMForwarding.error;
        }
        if (LDAPForwarding && LDAPForwarding.error) {
            return LDAPForwarding.error;
        }
        if (LDAPSForwarding && LDAPSForwarding.error) {
            return LDAPSForwarding.error;
        }
    };

    const domainNodesOptions = Object.values(domainNodes).map((domain) => {
        return { value: domain.id, label: domain.Name };
    });

    const fixedDomainNodesOptions = domainNodesOptions.map((domainOption) => {
        return { ...domainOption, className: SelectStyles.fixedOption };
    });
    const validateDomainName = (accountDomainName) => {
        if (!accountDomainName) {
            return 'Service account must have domain name';
        }

        if (!domainNodeId) {
            return 'No node found with the domain provided, please select this domain in one of the nodes before testing';
        }
    };

    const domainNameValue = domainName ? domainName.value : '';
    const domainShortNameValue = domainShortName ? domainShortName.value : '';
    const domainParts = domainNameValue.split('.');
    const baseDNParts = [];
    if (domainParts && domainParts.length > 0) {
        for (const part of domainParts) {
            baseDNParts.push('DC=' + part);
        }
    }

    const testMetadata = {
        nodesIds: Object.keys(domainNodes),
        domainId: domainId,
        domainData: {
            DomainBaseDN: baseDNParts.join(','),
            DomainName: domainNameValue,
            DomainShortName: domainShortNameValue
        }
    };
    const protocolsError = getProtocolsError();
    const showProtectedProtocols = kerberosForwarding || NTLMForwarding || LDAPForwarding || LDAPSForwarding;
    return (
        <>
            {domainName && <TextInput field={domainName} changed={dispatchUpdate} />}
            {domainShortName && <TextInput field={domainShortName} changed={dispatchUpdate} />}
            {ldapDomainName && <TextInput field={ldapDomainName} changed={dispatchUpdate} />}
            {ldapIp && useSecureLdap && (
                <SelectField
                    field={ldapIp}
                    changed={updateAndAddValueIfNeeded}
                    options={ldapServerOptions}
                    creatable={true}
                    clearable={true}>
                    <SwitchField field={useSecureLdap} changed={dispatchUpdate} marginTop="none" />
                </SelectField>
            )}
            {backupLdapIp && useSecureBackupLdap && (
                <SelectField
                    field={backupLdapIp}
                    changed={updateAndAddValueIfNeeded}
                    options={ldapServerOptions}
                    creatable={true}
                    clearable={true}>
                    <SwitchField field={useSecureBackupLdap} changed={dispatchUpdate} marginTop="none" />
                </SelectField>
            )}
            {assignedNodes && (
                <SelectField
                    field={assignedNodes}
                    changed={dispatchUpdate}
                    options={fixedDomainNodesOptions}
                    multichoice={true}
                    clearable={false}
                />
            )}

            {adsyncNodes && (
                <SelectField
                    field={adsyncNodes}
                    changed={dispatchUpdate}
                    options={domainNodesOptions}
                    multichoice={true}
                />
            )}

            {domainServiceAccount && (
                <Account
                    field={domainServiceAccount}
                    changed={dispatchUpdate}
                    testMetadata={{ ...testMetadata, accountType: TEST_ACCOUNTS.SERVICE_ACCOUNT }}
                    validateDomainName={validateDomainName}
                />
            )}
            {showProtectedProtocols && (
                <div className={styles.forwardingField}>
                    <Label value={'Protected Protocols'} />
                    <div className={styles.forwardingProtocols}>
                        {kerberosForwarding && (
                            <SwitchField field={kerberosForwarding} changed={dispatchUpdate} marginTop="small" />
                        )}
                        {NTLMForwarding && (
                            <SwitchField field={NTLMForwarding} changed={dispatchUpdate} marginTop="small" />
                        )}
                        {LDAPForwarding && (
                            <SwitchField field={LDAPForwarding} changed={dispatchUpdate} marginTop="small" />
                        )}
                        {LDAPSForwarding && (
                            <SwitchField field={LDAPSForwarding} changed={dispatchUpdate} marginTop="small" />
                        )}
                    </div>
                    {protocolsError && <div className={styles.errorText}>{protocolsError}</div>}
                    <Description value={'The authentication protocols to be protected by Silverfort in this Domain'} />
                </div>
            )}
            {certificateRequest && certificate && (
                <Certificate
                    id={domainId}
                    header={certificate.name}
                    desc="Certificate used to access Silverfort API servers in this Domain (AD Adapter, Federation services, API client applications, MFA)"
                    certificate={certificate}
                    certificateRequest={certificateRequest}
                    certType={CSR_TYPES.DOMAIN}
                    serverType={SERVER_TYPE.NODE}
                    changed={dispatchUpdate}
                    baseName={domainName && domainName.value}
                />
            )}
            {rootCAcertificate && (
                <Certificate
                    id={domainId}
                    header={rootCAcertificate.name}
                    certificate={rootCAcertificate}
                    serverType={SERVER_TYPE.DOMAIN_ROOT_CA}
                    changed={dispatchUpdate}
                    allowDelete
                    generateCSR={false}
                    baseName={`${domainName && domainName.value}_Root-CA`}
                />
            )}
            {allowDelete && (
                <Button onClick={handleDeleteClicked} className="my-5 mx-0" deleteButton>
                    {isNewDomain ? 'Discard' : 'Delete Domain'}
                </Button>
            )}
            {showDeleteMessage && (
                <ConfirmationMessage
                    title={'Delete Protected Domain'}
                    body={
                        <div>
                            {`Are you sure you want to delete ${
                                domainName && domainName.value ? `"${domainName.value}"` : 'this protected domain'
                            } ?`}
                            <span>You cannot undo this action.</span>
                            <span>
                                All assigned Domain Controllers are unprotected until assigned to a protected domain.
                            </span>
                            {!!pricingModulesState?.[PricingModules.PRIVILEGED_ACCESS_SECURITY].onPremEnabled && (
                                <>
                                    <span>
                                        This action will also re-enable all privileged accounts disabled by a JIT
                                        policy. It will take 2 minutes to complete.
                                    </span>
                                </>
                            )}
                        </div>
                    }
                    approveText={'Delete'}
                    cancelText={'Cancel'}
                    approveStyle={'danger'}
                    OnCancel={() => setShowDeleteMessage(false)}
                    OnApprove={deleteDomain}
                />
            )}
        </>
    );
};

export default React.memo(ProtectedDomain);
